/**
 * aminos-featured-product.js
 *
 * Controller for the AA - Featured product section. Responsibilities:
 *   1) Sync the hidden `selling_plan` input + the selected-card UI when the
 *      shopper switches between the subscription and one-time radios.
 *   2) Chip switcher (Product A / Product B) — toggles [hidden] across every
 *      pane with the matching data-aminos-fp-pane key in the section.
 *   3) Image gallery navigation — prev/next arrows + thumb click swap the
 *      main image and update the active thumb.
 *   4) Intercept the form submit and add to cart through the shared
 *      aminosCartClient queue (so the cart drawer refreshes natively via
 *      cartUpdate), then open the cart drawer.
 *
 * Delegated listeners on document so the controller survives any section
 * re-render (Shopify editor swaps section innerHTML on save).
 *
 * Graceful fallback: if aminosCartClient isn't available, the form keeps
 * its native action="/cart/add" so the page still adds + redirects to /cart.
 */
(function (global) {
  'use strict';

  // ===== Radio / selected state =====

  function refreshSelected(form) {
    form.querySelectorAll('[data-aminos-fp-option]').forEach((option) => {
      const radio = option.querySelector('[data-aminos-fp-radio]');
      option.classList.toggle('is-selected', !!(radio && radio.checked));
    });
  }

  function syncSellingPlan(form) {
    const planInput = form.querySelector('[data-aminos-fp-selling-plan]');
    if (!planInput) return;
    const active = form.querySelector('[data-aminos-fp-radio]:checked');
    planInput.value = (active && active.dataset.sellingPlanId) || '';
  }

  // ===== Quantity-aware pricing =====

  // Multiply the first number found in a money string by qty, preserving the
  // currency symbol, suffix (e.g. "/month", " Per Day") and decimal places.
  // Assumes "." decimal / "," thousands (USD-style). Works for both the native
  // plan prices and the merchant's text overrides.
  function scaleMoney(text, qty) {
    if (!text) return text;
    return text.replace(/[\d.,]+/, (token) => {
      const dot = token.lastIndexOf('.');
      const decimals = dot === -1 ? 0 : token.length - dot - 1;
      const value = parseFloat(token.replace(/,/g, ''));
      if (isNaN(value)) return token;
      const parts = (value * qty).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  }

  function getQuantity(form) {
    const checked = form.querySelector('input[name="quantity"]:checked') || form.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt((checked && checked.value) || '1', 10) || 1);
  }

  // Rescale every price in the form from its captured unit (qty 1) value.
  function applyQuantityPricing(form, qty) {
    form.querySelectorAll('[data-aminos-fp-price]').forEach((el) => {
      if (el.dataset.fpBase === undefined) el.dataset.fpBase = el.textContent.trim();
      el.textContent = scaleMoney(el.dataset.fpBase, qty);
    });
  }

  // ===== Chip switcher =====

  function switchPane(section, key) {
    section.querySelectorAll('[data-aminos-fp-pane]').forEach((pane) => {
      pane.hidden = pane.getAttribute('data-aminos-fp-pane') !== key;
    });
    section.querySelectorAll('[data-aminos-fp-chip]').forEach((chip) => {
      const active = chip.dataset.key === key;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  // ===== Gallery: prev / next / thumb click =====

  function setMainImage(gallery, thumb) {
    if (!thumb) return;
    const main = gallery.querySelector('[data-aminos-fp-main]');
    if (!main) return;
    if (thumb.dataset.src) main.src = thumb.dataset.src;
    if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
    if (thumb.dataset.alt) main.alt = thumb.dataset.alt;

    gallery.querySelectorAll('[data-aminos-fp-thumb]').forEach((t) => {
      t.classList.toggle('is-active', t === thumb);
    });

    // Scroll the active thumb into view inside the horizontal strip
    if (typeof thumb.scrollIntoView === 'function') {
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  function stepGallery(gallery, direction) {
    const thumbs = Array.from(gallery.querySelectorAll('[data-aminos-fp-thumb]'));
    if (thumbs.length === 0) return;
    const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
    const next = (current + direction + thumbs.length) % thumbs.length;
    setMainImage(gallery, thumbs[next]);
  }

  // ===== Init on load / editor re-render =====

  function initSection(section) {
    section.querySelectorAll('[data-aminos-fp-form]').forEach((form) => {
      refreshSelected(form);
      syncSellingPlan(form);
      applyQuantityPricing(form, getQuantity(form));
    });
    // Make sure each gallery has an active thumb (first one by default)
    section.querySelectorAll('[data-aminos-fp-gallery]').forEach((gallery) => {
      const active = gallery.querySelector('[data-aminos-fp-thumb].is-active');
      if (!active) {
        const first = gallery.querySelector('[data-aminos-fp-thumb]');
        if (first) first.classList.add('is-active');
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-aminos-fp-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:select', initAll);

  // ===== Delegated event handlers =====

  document.addEventListener('change', (event) => {
    // Quantity pill: move the selected highlight.
    const qtyRadio = event.target.closest('[data-aminos-fp-qty]');
    if (qtyRadio) {
      const group = qtyRadio.closest('.aminos-featured-product__qty');
      if (group) {
        group.querySelectorAll('.aminos-featured-product__qty-option').forEach((opt) => {
          opt.classList.toggle('is-selected', opt.querySelector('[data-aminos-fp-qty]') === qtyRadio);
        });
      }
      const form = qtyRadio.closest('[data-aminos-fp-form]');
      if (form) applyQuantityPricing(form, Math.max(1, parseInt(qtyRadio.value, 10) || 1));
      return;
    }

    const radio = event.target.closest('[data-aminos-fp-radio]');
    if (!radio) return;
    const form = radio.closest('[data-aminos-fp-form]');
    if (!form) return;
    refreshSelected(form);
    syncSellingPlan(form);
  });

  document.addEventListener('click', (event) => {
    // Chip switcher
    const chip = event.target.closest('[data-aminos-fp-chip]');
    if (chip) {
      const section = chip.closest('[data-aminos-fp-section]');
      if (section) switchPane(section, chip.dataset.key);
      return;
    }

    // Thumb click → main image
    const thumb = event.target.closest('[data-aminos-fp-thumb]');
    if (thumb) {
      const gallery = thumb.closest('[data-aminos-fp-gallery]');
      if (gallery) setMainImage(gallery, thumb);
      return;
    }

    // Prev/next gallery arrows
    const prev = event.target.closest('[data-aminos-fp-thumb-prev]');
    if (prev) {
      const gallery = prev.closest('[data-aminos-fp-gallery]');
      if (gallery) stepGallery(gallery, -1);
      return;
    }
    const next = event.target.closest('[data-aminos-fp-thumb-next]');
    if (next) {
      const gallery = next.closest('[data-aminos-fp-gallery]');
      if (gallery) stepGallery(gallery, 1);
      return;
    }

    // Nutrition popup: open
    const nutritionOpen = event.target.closest('[data-aminos-fp-nutrition-open]');
    if (nutritionOpen) {
      const scope = nutritionOpen.closest('[data-aminos-fp-section]') || document;
      const modal = scope.querySelector('[data-aminos-fp-nutrition-modal]');
      if (modal && typeof modal.showModal === 'function') modal.showModal();
      return;
    }

    // Nutrition popup: close (close button, or click on the backdrop area)
    const modalEl = event.target.closest('[data-aminos-fp-nutrition-modal]');
    if (event.target.closest('[data-aminos-fp-nutrition-close]') || event.target === modalEl) {
      const dialog = event.target.closest('[data-aminos-fp-nutrition-modal]');
      if (dialog && typeof dialog.close === 'function') dialog.close();
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-aminos-fp-form]');
    if (!form) return;
    // If this pane is hidden (chip switched away from it), don't add the
    // wrong product. Only the visible form is the user's intent.
    const pane = form.closest('[data-aminos-fp-pane]');
    if (pane && pane.hidden) return;
    const cartClient = global.aminosCartClient;
    if (!cartClient) return; // no client → fall back to native /cart/add

    event.preventDefault();

    const variantId = Number(form.dataset.variantId);
    if (!variantId) {
      console.warn('[aminos-featured-product] Missing data-variant-id');
      return;
    }

    const planInput = form.querySelector('[data-aminos-fp-selling-plan]');
    const sellingPlanId = planInput && planInput.value ? Number(planInput.value) : null;

    // Quantity: selected radio (pill) or the hidden fallback input.
    const qtyField =
      form.querySelector('input[name="quantity"]:checked') || form.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, parseInt((qtyField && qtyField.value) || '1', 10) || 1);

    const btn = form.querySelector('[data-aminos-fp-submit]');
    const btnLabel = btn ? btn.querySelector('span') : null;
    const originalText = btnLabel ? btnLabel.textContent : null;

    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }
    if (btnLabel) btnLabel.textContent = 'Adding…';

    try {
      const payload = { id: variantId, quantity: quantity };
      if (sellingPlanId) payload.selling_plan = sellingPlanId;
      await cartClient.add(payload);

      const destination = form.dataset.aminosFpDestination || 'drawer';
      if (destination === 'checkout') {
        const discount = (form.dataset.aminosFpDiscount || '').trim();
        window.location.href = discount
          ? '/checkout?discount=' + encodeURIComponent(discount)
          : '/checkout';
      } else if (destination === 'cart') {
        window.location.href = form.dataset.aminosFpCartUrl || '/cart';
      } else {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: true },
          }));
          window.setTimeout(() => {
            const refreshedDrawer = document.querySelector('cart-drawer');
            if (refreshedDrawer && !refreshedDrawer.hasAttribute('hidden')) return;
            const trigger = document.querySelector('.cart-drawer-button[aria-controls="CartDrawer"], [data-cart-trigger]');
            if (trigger) trigger.click();
          }, 250);
        }
      }
    } catch (err) {
      console.error('[aminos-featured-product] Add to cart failed', err);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
      if (btnLabel && originalText != null) btnLabel.textContent = originalText;
    }
  });
})(window);
